import React, { useState } from "react";
import styled, { keyframes } from "styled-components";

// ===== Animations =====
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(5px); }
  to { opacity: 1; transform: translateY(0); }
`;

// ===== Styled Components =====
const Container = styled.div`
  position: relative;
  display: inline-block;
`;


const Modal = styled.div`
  position: absolute;
  top: 8px;
  left: 60px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  padding: 20px 20px 10px;
  width: 320px;
  animation: ${fadeIn} 0.25s ease-in-out;
  z-index: 100;
`;

const ModalContent = styled.div`
  h4 {
    margin: 0 0 8px;
    font-weight: 600;
    font-size: 15px;
  }

  p {
    font-size: 14px;
    line-height: 1.5;
    color: #333;
  }
`;

const Card = styled.div`
  margin-top: 15px;
  border-radius: 12px;
  overflow: hidden;
  width: 100%;
  height: 140px;
  background: #eee;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const CardImage = styled.img`
  width: 100%;
  height: auto;
  object-fit: contain;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top: 1px solid #eee;
  padding-top: 10px;
  margin-top: 15px;
  font-size: 13px;
  color: #666;
`;

const NavButton = styled.button`
  border: none;
  background: transparent;
  color: #333;
  cursor: pointer;
  font-weight: 500;
  opacity: ${(p) => (p.disabled ? 0.4 : 1)};
  pointer-events: ${(p) => (p.disabled ? "none" : "auto")};
  transition: 0.3s ease all;

  &:hover {
    opacity: 0.7;
  }

  &[disabled] {
    opacity: 0;
  }
`;

// ===== Animated Icon (Question ↔ Cross) =====
const ToggleIconWrapper = styled.div`
    position: relative;
    width: 40px;
    height: 40px;
    cursor: pointer;
    font-size: 14px;
    display: flex;
    align-items: center;
    gap: 6px;
    transition: all 0.2s ease;
    margin: 10px;

    svg {
        width: 100%;
        height: 100%;
    }
`;

const SvgBase = styled.svg`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  transition: opacity 0.25s ease;
  opacity: ${(p) => (p.visible ? 1 : 0)};
`;


const ToggleButton = ({ open, onClick }) => (
  <ToggleIconWrapper onClick={onClick}>
    {/* Question mark */}
    <SvgBase
      viewBox="0 0 48 48"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      visible={!open}
    >
      <path
        d="M24 4C35.0457 4 44 12.9543 44 24C44 35.0457 35.0457 44 24 44C12.9543 44 4 35.0457 4 24C4 12.9543 12.9543 4 24 4ZM24 6.5C14.335 6.5 6.5 14.335 6.5 24C6.5 33.665 14.335 41.5 24 41.5C33.665 41.5 41.5 33.665 41.5 24C41.5 14.335 33.665 6.5 24 6.5ZM24.25 32C25.0784 32 25.75 32.6716 25.75 33.5C25.75 34.3284 25.0784 35 24.25 35C23.4216 35 22.75 34.3284 22.75 33.5C22.75 32.6716 23.4216 32 24.25 32ZM24.25 13C27.6147 13 30.5 15.8821 30.5 19.2488C30.502 21.3691 29.7314 22.7192 27.8216 24.7772L26.8066 25.8638C25.7842 27.0028 25.3794 27.7252 25.3409 28.5793L25.3379 28.7411L25.3323 28.8689L25.3143 28.9932C25.2018 29.5636 24.7009 29.9957 24.0968 30.0001C23.4065 30.0049 22.8428 29.4493 22.8379 28.7589C22.8251 26.9703 23.5147 25.7467 25.1461 23.9739L26.1734 22.8762C27.5312 21.3837 28.0012 20.503 28 19.25C28 17.2634 26.2346 15.5 24.25 15.5C22.3307 15.5 20.6142 17.1536 20.5055 19.0587L20.4935 19.3778C20.4295 20.0081 19.8972 20.5 19.25 20.5C18.5596 20.5 18 19.9404 18 19.25C18 15.8846 20.8864 13 24.25 13Z"
        fill="#212121"
      />
    </SvgBase>

    {/* Cross */}
    <SvgBase
      viewBox="0 0 48 48"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      visible={open}
    >
      <circle cx="24" cy="24" r="20" stroke="#212121" strokeWidth="2" />
      <path
        d="M16 16L32 32M32 16L16 32"
        stroke="#212121"
        strokeWidth="3"
        strokeLinecap="round"
      />
    </SvgBase>
  </ToggleIconWrapper>
);

// ===== Component =====
const StepModal = ({data}) => {

  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState(0);

  const nextStep = () => current < data.length - 1 && setCurrent(current + 1);
  const prevStep = () => current > 0 && setCurrent(current - 1);
  const toggleModal = () => {
  setOpen((prev) => {
    const nextState = !prev;
    if (!nextState) setCurrent(0);
    return nextState;
  });
};


  return (
    <Container>
    <ToggleButton open={open}  onClick={toggleModal}/>

      {open && data?.length && (
        <Modal>

          <ModalContent>
            <h4>{data[current].title}</h4>
            <p>{data[current].content}</p>

            
              { data[current].media_type === 'image' && data[current].image.url && <Card><CardImage src={data[current].image.url} alt={data[current].title} /></Card>}
               { data[current].media_type === 'video' && data[current].video && <Card><div
          className="video-wrapper"
          dangerouslySetInnerHTML={{ __html: data[current].video }}
        /></Card>}
            
          </ModalContent>

          <ModalFooter>
            <NavButton onClick={prevStep} disabled={current === 0}>
              ‹ Previous
            </NavButton>
            <span>
              {current + 1} / {data.length}
            </span>
            <NavButton onClick={nextStep} disabled={current === data.length - 1}>
              Next ›
            </NavButton>
          </ModalFooter>
        </Modal>
      )}
    </Container>
  );
};

export default StepModal;
